import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { annonceService } from '../../api';
import './ListeAnnonce.css';
import { FiMapPin, FiHeart, FiStar } from 'react-icons/fi';

const ListeAnnonce = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentAnnonces = async () => {
      try {
        setLoading(true);
        console.log('🔍 Début récupération des annonces récentes...');
        
        // Récupérer toutes les annonces comme dans Annonce.js
        let allAnnonces = [];
        let currentPage = 1;
        let hasMoreData = true;
        
        while (hasMoreData) {
          try {
            console.log(`📄 Récupération page ${currentPage}...`);
            const response = await annonceService.getAll(currentPage);
            
            // Extraire les annonces selon différents formats possibles
            let annoncesPage = [];
            
            if (response) {
              // Format 1: response.data est un tableau
              if (Array.isArray(response.data)) {
                annoncesPage = response.data;
              }
              // Format 2: response.data.data est un tableau
              else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                annoncesPage = response.data.data;
              }
              // Format 3: response.data est un objet avec des annonces
              else if (response.data && response.data.annonces && Array.isArray(response.data.annonces)) {
                annoncesPage = response.data.annonces;
              }
              // Format 4: response est directement un tableau
              else if (Array.isArray(response)) {
                annoncesPage = response;
              }
            }
            
            console.log(`✅ Page ${currentPage}: ${annoncesPage.length} annonces trouvées`);
            
            if (annoncesPage.length > 0) {
              // Traiter les images pour cette page - MÊME LOGIQUE QUE Annonce.js
              const annoncesAvecImages = annoncesPage.map(annonce => {
                const annonceAvecImage = { ...annonce };
                
                // Construction de l'URL d'image - LOGIQUE IDENTIQUE À Annonce.js
                if (annonce.image) {
                  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                  
                  if (annonce.image.startsWith('http')) {
                    annonceAvecImage.imageUrl = annonce.image;
                  } else {
                    annonceAvecImage.imageUrl = `${baseUrl}/storage/${
                      annonce.image.startsWith('storage/') 
                        ? annonce.image.replace('storage/', '') 
                        : annonce.image
                    }`;
                  }
                } else {
                  annonceAvecImage.imageUrl = '/default-annonce.jpg';
                }
                
                console.log(`ListeAnnonce - Annonce ${annonce.id} - URL image: ${annonceAvecImage.imageUrl}`);
                return annonceAvecImage;
              });
              
              allAnnonces = [...allAnnonces, ...annoncesAvecImages];
            }
            
            // Vérifier s'il y a plus de pages
            const totalPages = response?.data?.last_page || 
                              response?.last_page || 
                              response?.data?.meta?.last_page || 
                              1;
            
            if (currentPage >= totalPages || annoncesPage.length === 0) {
              hasMoreData = false;
              console.log(`🛑 Fin de la pagination à la page ${currentPage}`);
            } else {
              currentPage++;
            }
            
          } catch (pageError) {
            console.error(`❌ Erreur page ${currentPage}:`, pageError);
            hasMoreData = false;
          }
        }
        
        console.log(`✅ Total récupéré: ${allAnnonces.length} annonces`);
        
        // Trier les annonces par date de création (les plus récentes en premier) - MÊME LOGIQUE
        allAnnonces.sort((a, b) => {
          const dateA = new Date(a.created_at || a.date_creation || 0);
          const dateB = new Date(b.created_at || b.date_creation || 0);
          return dateB - dateA; // Ordre décroissant (plus récent d'abord)
        });
        
        // Prendre les 3 premières annonces les plus récentes
        const recentAnnonces = allAnnonces.slice(0, 3);
        console.log(`✅ ${recentAnnonces.length} annonces récentes à afficher`);
        
        setAnnonces(recentAnnonces);
        
      } catch (err) {
        console.error('❌ Erreur globale ListeAnnonce:', err);
        setError(err.message || 'Erreur lors du chargement des annonces');
        
        // Fallback: essayer de récupérer juste la première page
        try {
          console.log('🔄 Tentative de récupération simple (fallback)...');
          const fallbackResponse = await annonceService.getAll(1);
          
          let fallbackAnnonces = [];
          if (fallbackResponse) {
            if (Array.isArray(fallbackResponse.data)) {
              fallbackAnnonces = fallbackResponse.data;
            } else if (fallbackResponse.data && fallbackResponse.data.data && Array.isArray(fallbackResponse.data.data)) {
              fallbackAnnonces = fallbackResponse.data.data;
            }
          }
          
          // Traiter les images pour le fallback - MÊME LOGIQUE
          const annoncesAvecImages = fallbackAnnonces.map(annonce => {
            const annonceAvecImage = { ...annonce };
            
            if (annonce.image) {
              const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
              
              if (annonce.image.startsWith('http')) {
                annonceAvecImage.imageUrl = annonce.image;
              } else {
                annonceAvecImage.imageUrl = `${baseUrl}/storage/${
                  annonce.image.startsWith('storage/') 
                    ? annonce.image.replace('storage/', '') 
                    : annonce.image
                }`;
              }
            } else {
              annonceAvecImage.imageUrl = '/default-annonce.jpg';
            }
            
            return annonceAvecImage;
          });
          
          // Trier et prendre les 3 premières
          const sortedFallback = annoncesAvecImages.sort((a, b) => {
            const dateA = new Date(a.created_at || a.date_creation || 0);
            const dateB = new Date(b.created_at || b.date_creation || 0);
            return dateB - dateA;
          }).slice(0, 3);
          
          setAnnonces(sortedFallback);
        } catch (fallbackError) {
          console.error('❌ Erreur fallback ListeAnnonce:', fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAnnonces();
  }, []);

  const handleImageError = (annonceId) => {
    console.error(`ListeAnnonce - Image error for annonce ${annonceId}`);
    setImageErrors(prev => ({ ...prev, [annonceId]: true }));
  };

  // Fonction pour obtenir l'URL de l'image - MÊME LOGIQUE QUE Annonce.js
  const getImageUrl = (annonce) => {
    if (imageErrors[annonce.id]) {
      console.log(`ListeAnnonce - Using default image for annonce ${annonce.id}`);
      return '/default-annonce.jpg';
    }
    
    // Utiliser l'URL prétraitée si disponible
    if (annonce.imageUrl) {
      console.log(`ListeAnnonce - Using preprocessed imageUrl for annonce ${annonce.id}: ${annonce.imageUrl}`);
      return annonce.imageUrl;
    }
    
    // Fallback: construire l'URL
    if (annonce.image) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const imageUrl = `${baseUrl}/storage/${
        annonce.image.startsWith('storage/') 
          ? annonce.image.replace('storage/', '') 
          : annonce.image
      }`;
      console.log(`ListeAnnonce - Constructed image URL for annonce ${annonce.id}: ${imageUrl}`);
      return imageUrl;
    }
    
    console.log(`ListeAnnonce - No image for annonce ${annonce.id}, using default`);
    return '/default-annonce.jpg';
  };

  // Fonction pour formater la date - MÊME LOGIQUE QUE Annonce.js
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Aujourd'hui";
    } else if (diffDays === 1) {
      return "Hier";
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  if (loading) {
    return (
      <div className="liste-annonce-container">
        <h2 className="liste-annonce-title">Nouvelles annonces</h2>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des nouvelles annonces...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="liste-annonce-container">
        <h2 className="liste-annonce-title">Nouvelles annonces</h2>
        <div className="error-container">
          <p className="error-message">Erreur: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="liste-annonce-container">
      <h2 className="liste-annonce-title">Nouvelles annonces</h2>
      
      <div className="liste-annonce-grid">
        {annonces.length > 0 ? (
          annonces.map((annonce) => {
            const imageUrl = getImageUrl(annonce);
            const datePublication = formatDate(annonce.created_at || annonce.date_creation);
            
            return (
              <div 
                key={annonce.id} 
                className="liste-annonce-card"
                onClick={() => navigate(`/annonces/${annonce.id}`)}
              >
                <div className="liste-card-image-container">
                  <img 
                    src={imageUrl}
                    alt={annonce.titre}
                    className="liste-annonce-image"
                    onError={() => {
                      console.error(`ListeAnnonce - Échec chargement image annonce ${annonce.id}: ${imageUrl}`);
                      handleImageError(annonce.id);
                    }}
                    onLoad={() => console.log(`ListeAnnonce - Image chargée pour annonce ${annonce.id}: ${imageUrl}`)}
                  />
                  <button 
                    className="liste-wishlist-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      // Gérer l'ajout aux favoris
                    }}
                  >
                    <FiHeart className="icon" />
                  </button>
                  
                  {/* Badge "Nouveau" pour les annonces récentes (moins de 7 jours) */}
                  {(annonce.created_at || annonce.date_creation) && 
                   (new Date() - new Date(annonce.created_at || annonce.date_creation)) < (7 * 24 * 60 * 60 * 1000) && (
                    <div className="liste-new-badge">
                      Nouveau
                    </div>
                  )}
                </div>
                
                <div className="liste-card-details">
                  <div className="liste-card-header">
                    <h3>{annonce.titre}</h3>
                    <div className="liste-rating">
                      <FiStar className="icon" />
                      <span>4.8</span>
                    </div>
                  </div>
                  
                  <p className="liste-location">
                    <FiMapPin className="icon" /> {annonce.ville}
                  </p>
                  
                  {/* Affichage de la date de publication */}
                  {datePublication && (
                    <p className="liste-publication-date">
                      Publié {datePublication}
                    </p>
                  )}
                  
                  <p className="liste-price">
                    {annonce.prix} € <span>par service</span>
                  </p>
                  
                  <div className="liste-service-tag">
                    {annonce.sous_service?.nom || 'Non catégorisé'}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-annonces">
            <p>Aucune nouvelle annonce pour le moment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListeAnnonce;